import { Button } from "@component-ui/utility/Button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@component-ui/utility/Dialog";
import { Input } from "@component-ui/utility/Input";
import { Label } from "@component-ui/utility/Label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@component-ui/utility/Tooltip";
import { FORM_KEY } from "@constant/form";
import useUpdateDocumentTemplateMutate from "@hooks/template/useUpdateDocumentTemplateMutate";
import { Loader2, Pencil } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { FC, FormEvent, ReactElement, useEffect, useState } from "react";
import { DocumentTemplate } from "types/backend/document_template";

type UpdateDocumentTemplateDialogProps = {
    documentTemplate: DocumentTemplate;
    ButtonElement?: ReactElement;
    onClose?: () => void;
};

const UpdateDocumentTemplateDialogCapture: FC = () => {
    const posthog = usePostHog();
    useEffect(() => {
        posthog?.capture("$pageview", { pageName: "/template/edit" });
    }, []);

    return null;
};

const UpdateDocumentTemplateDialog: FC<UpdateDocumentTemplateDialogProps> = ({
    documentTemplate,
    ButtonElement = (
        <Button variant={"ghost"} size={"icon"}>
            <Pencil className="w-4 h-4" />
        </Button>
    ),
    onClose,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { mutate: updateDocumentTemplate, isPending: isUpdating } =
        useUpdateDocumentTemplateMutate(documentTemplate.id);

    function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        updateDocumentTemplate(formData, {
            onSuccess: () => {
                setIsOpen(false);
            },
        });
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(e) => {
                setIsOpen(e);
                if (!e && onClose) {
                    onClose();
                }
            }}
        >
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <DialogTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <TooltipTrigger asChild>{ButtonElement}</TooltipTrigger>
                    </DialogTrigger>
                    <TooltipContent side="bottom">Update name</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Update template</DialogTitle>
                    <UpdateDocumentTemplateDialogCapture />
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <Label htmlFor={FORM_KEY.DOCUMENT_TEMPLATE.TITLE}>
                        Template Title
                    </Label>
                    <Input
                        name={FORM_KEY.DOCUMENT_TEMPLATE.TITLE}
                        id={FORM_KEY.DOCUMENT_TEMPLATE.TITLE}
                        defaultValue={documentTemplate.title}
                    />
                    <Button
                        type="submit"
                        disabled={isUpdating}
                        className="w-full"
                    >
                        {isUpdating && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Update
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default UpdateDocumentTemplateDialog;
